import {EnumToMapping, getEnumNameValueMap, Paths} from "common";
import {formatDate, formatDateStr} from "common/util/date";
import _, {get, intersection, isEmpty} from "lodash";
import {Person} from "core/domain";
import {parseISO} from "date-fns";
import {StaffingAction} from "./staffing-plan/domain";
import {TreatmentHoldSubmitInput} from "./offer-at-risk/family-init-hold/TreatmentHoldFormModal";

import {SPLIT_DELIMITER} from "../../placement/const";
import { AnticipatedDischargeInfo } from "./discharge/domain";


// Define YesNoOption enum and map for reuse across the application
export enum YesNoOption {
    YES = "YES",
    NO = "NO"
}

export const YesNoOptionMap = new Map<string, string>([
    [YesNoOption.YES, "Yes"],
    [YesNoOption.NO, "No"]
]);

// Define DischargeInitiator enum and map
export enum DischargeInitiator {
    PROVIDER = "PROVIDER",
    CLIENT = "CLIENT",
    CAREGIVER = "CAREGIVER"
}

export const DischargeInitiatorOptionMap = new Map<string, string>([
    [DischargeInitiator.PROVIDER, "Provider"],
    [DischargeInitiator.CLIENT, "Client"],
    [DischargeInitiator.CAREGIVER, "Caregiver"]
]);

export const CLIENT_PROFILE_CONTACTS_PATH = "contacts"
export const CLIENT_PROFILE_AUTHORIZATIONS_PATH = "authorizations"
export const CLIENT_PROFILE_CLIENT_REPORTS_PATH = "clientReports"
export const CLIENT_PROFILE_DOCUMENT_FILES_PATH = "documentFiles"
export const CLIENT_PROFILE_SUBMITTED_REQUEST_PATH = "submittedRequest"
export const CLIENT_PROFILE_PROGRESS_PORTRAITS_PATH = "progressPortraits"
export const CLIENT_PROFILE_CARE_TEAM_MEMBERS_PATH = "careTeamMembers"
export const CLIENT_PROFILE_REFERRAL_OFFER_PATH = "referralOffers"
export const CLIENT_PROFILE_UTILIZATION_PATH = "utilization"

export type UtilizationAuthorizationInfo = {
    guid: string;
    authName: string;
    bhpnAuthNumber: string;
    startDate: string;
    endDate: string;
    status: string;
    serviceLine?: string | null;
    funder?: string | null;
    allServiceType?: string[] | null;
}

export type UtilizedHoursEntry = {
    procedureCode: string;
    practitionerRole: string | null;
    // 'Minimum Hours Authorized' from the authorization item.
    authorizedHours: number;
    // 'Direct Hours Required' from the Sparkle authorization item.
    directCareHoursRequired: number;
    // 'Direct Hours Billed' from the Sparkle authorization item.
    utilizedHours: number;
}

export type UtilizationPeriod = {
    periodStart: string;
    periodEnd: string;
    utilizedHours: UtilizedHoursEntry[];
}

export type AuthorizationUtilization = {
    authorization: UtilizationAuthorizationInfo;
    periods: UtilizationPeriod[];
}

export interface ClientColumn {
    name: string;
    attr: keyof ClientInfo;
    type?: string;
}

export interface AuthorizationColumn {
    name: string;
    attr: Paths<AuthorizationInfo>;
    span: number;
}

export interface ClientReportColumn {
    name: string;
    attr: Paths<ClientReportInfo>;
    format: 'date' | 'string' | 'string[]';
}

export interface ClientReportFileColumn {
    name: string;
    attr: keyof ClientReportFile;
}

export interface ClientDocumentColumn {
    name: string;
    attr: keyof ClientDocument;
}

export type ClientRelatedContact = {
    primaryContact: boolean;
    relationshipToClient: string;
    name: string;
    email: string;
    homePhone: string;
    mobilePhone: string;
    workPhone: string;
    street: string;
    city: string;
    stateCode: string;
    zipCode: string;
};

export type TeamContact = {
    guid: string
    firstName: string;
    lastName: string;
    memberRole: string;
    email: string;
    phone: string;
    mobile: string;
    startDate: string;
    endDate: string;
}

export interface TeamContactColumn {
    name: string;
    attr: keyof TeamContact;
}

export type ClientInfo = {
    bhpnClientId: string;
    guid: string;
    genderInfo: string;
    name: string;
    fullName: string;
    birthDate: string;
    email: string;
    status: string;
    preferredLanguages: string[];
    medicalRecordNumber: string;
    age: string;
    city: string;
    clientRelatedContacts: ClientRelatedContact[];
    referralAcceptedDate: string | null;
    anticipatedDischargeDate: string | null;
    providerAnticipatedDischargeDate: AnticipatedDischargeInfo | null;
    dualProvider?: boolean;
    dualProviderOtherServices?: DualProviderOtherServices | null;
    latestCareCoordinationRequest?: CareCoordinationRequestStatus | null;
}

export type CareCoordinationRequestStatus = {
    guid: string;
    status: string;
    submittedDate: string;
}

export type DualProviderOtherServices = {
    clientId?: string | null;
    providerId?: string | null;
    providerName?: string | null;
    serviceType?: string | null;
}

export type ClientCareDates = {
    referralAcceptedDate: string | null;
    anticipatedDischargeDate: string | null;
}

export type ParentCaseInfo = {
    sfId: string;
    caseNumber: string;
    guid: string;
    recordType: string;
    status: string;
    clientMrn: string;
    clientCity: string;
    client: ClientProfile;
    placementDate: string;
    activeStatus: string;
    isAtRisk: boolean;
    clientGuid: string;
    serviceTypes: string[];
}

// export type CaseProviderAgencyInfo = {
//     guid: string;
//     sfId: string;
//     name: string;
//     status: string;
// }

export type ClientProfile = {
    guid: string;
    sfId: string;
    fullName: string;
    email: string;
    age: string;
}

export interface ParentCaseInfoColumn {
    name: string;
    attr: Paths<ParentCaseInfo>;
}

export interface ParentCaseSortBy {
    column: Paths<ParentCaseInfo>
    order: SortOrder;
}

export interface AuthorizationSortBy {
    column: Paths<AuthorizationInfo>
    order: SortOrder
}

export interface ClientReportSortBy {
    column: Paths<ClientReportInfo>
    order: SortOrder
}

export interface ClientDocumentSortBy {
    column: Paths<ClientDocument>
    order: SortOrder
}

export interface CareTeamSortBy {
    column: Paths<TeamContact>
    order: SortOrder
}

export enum SortOrder {
    DESC = 'desc',
    ASC = 'asc'
}

export interface ValidationResult {
    status: boolean;
    message: string;
}

export enum AuthorizationServiceType {
    Three_Tier_ABA = '3-Tier ABA',
    Three_Tier_OR_Parent_Training = '3-Tier ABA OR ABA Parent Training',
    Three_Tier_OR_Parent_Led = '3-Tier ABA OR Parent Led ABA',
    ABA_Parent_Training = 'ABA Parent Training',
    ABA_Services_Severe_Behaviors = 'ABA Services - Severe Behaviors',
    Chat = 'Chat',
    Connect = 'Connect',
    Group_ABA = 'Group ABA',
    Parent_Led_ABA = 'Parent Led ABA',
    Social_Skills_Group = 'Social Skills Group',
    Compass_Behavioral = 'Compass-Behavioral',
    RUBI = 'RUBI',
    Calibrate = 'Calibrate ABA',
    Psychotherapy = 'Psychotherapy',
    Compass_Psychotherapy = 'Compass Psychotherapy'
}

export enum AuthorizationAssessmentType {
    Chat_Assessment = "Chat Assessment",
    Comprehensive_ABA_Assessment = 'Comprehensive ABA Assessment',
    Connect_Assessment = 'Connect Assessment',
    Initial_Behavioral_Health_Assessment = 'Initial Behavioral Health Assessment',
    PLAY_Project_Assessment = 'PLAY Project Assessment',
    Severe_Behaviors_Assessment = 'Severe Behaviors Assessment',
    Social_Skills_Group_Assessment = 'Social Skills Group Assessment',
    RUBI_Assessment = 'RUBI Assessment',
    Compass_Behavioral_Assessment = 'Compass-Behavioral Assessment',
    Calibrate_ABA_Assessment = 'Calibrate ABA Assessment',
    Psychotherapy_Assessment = 'Psychotherapy Assessment'
}

export const {
    nameToValue: AuthorizationAssessmentTypeMap,
} = EnumToMapping(AuthorizationAssessmentType)

export const ParentCaseServiceType = {
    ...AuthorizationServiceType,
    ...AuthorizationAssessmentType,
};

const NoLongerOfferedAuthServiceTypes: (AuthorizationServiceType | AuthorizationAssessmentType)[] = [
    AuthorizationServiceType.Psychotherapy,
    AuthorizationServiceType.Compass_Psychotherapy,
    AuthorizationAssessmentType.Severe_Behaviors_Assessment,
    AuthorizationAssessmentType.PLAY_Project_Assessment,
    AuthorizationAssessmentType.Initial_Behavioral_Health_Assessment,
    AuthorizationAssessmentType.Calibrate_ABA_Assessment,
    AuthorizationAssessmentType.Psychotherapy_Assessment,
    AuthorizationServiceType.ABA_Services_Severe_Behaviors
];

export const {
    valueToName: ParentCaseServiceTypeValueToName,
    nameToValue: ParentCaseServiceTypeNameToValue,
    nameToValueWithDefault: ParentCaseServiceTypeNameToValueWithDefault,
} = EnumToMapping(ParentCaseServiceType, {value: "", label: "All Service Type"})

export const CurrentAcitveAuthorizationServiceTypeFilterOption = new Map<string, string>(
    Array.from(ParentCaseServiceTypeNameToValue.entries()).filter(
        (entry: [string, string]) => !NoLongerOfferedAuthServiceTypes.includes(entry[1] as AuthorizationServiceType | AuthorizationAssessmentType)));


export enum ClientDocumentType {
    Addendum_Report = "Addendum Report",
    Assessment_Report = "Assessment Report",
    BHT_Initial_Assessment = "BHT Initial Assessment Report",
    Client_Document = "Client Document",
    Client_Packet = "Client Packet",
    Clinical_Packet = "Clinical Packet",
    Discharge_Report = "Discharge Report",
    Progress_Report = "Progress Report",
    Therapy_Approval_Letter = "Therapy Approval Letter",
    Transfer_Reevaluation_Report = "Transfer Reevaluation Report",
    Transfer_Report = "Transfer Report",
    Vineland = "Vineland",
}

export const {
    nameToValueWithDefault: ClientDocumentTypeMap,
}
    = EnumToMapping(ClientDocumentType, {value: "", label: "All Documents"})


export type AuthorizationInfo = {
    guid: string;
    numOfTier: number;
    sfId: string;
    status: string;
    startDate: string;
    endDate: string;
    closedReason: string;

    careMediatedMidMinHours: number;
    careMediatedMidHours: number;

    careMediatedHighMinHours: number;
    careMediatedHighHours: number;

    practDirectBeneficialHours: number;
    practMidBeneficialHours: number;
    practHighBeneficialHours: number;
    practGroupBeneficialHours: number;

    practDirectOptimalHours: number;
    practMidOptimalHours: number;
    practHighOptimalHours: number;
    practGroupOptimalHours: number;

    bhpnAuthNumber: string;
    diagnosis: string;
    serviceLine: string;
    reportDueDate: string;
    reportSubmittedDate: string;
    reportStatus: string;
    assessmentType: string;
    assessmentCode: string;
    recordType: string;

    allServiceType: string[];
    isAtRisk: boolean;
    hasBilledHours: boolean | null;

    isDraft1: boolean;
    serviceType1: string;
    startDate1: string;
    endDate1: string;
    directCareHour1: number;
    midLevelHour1: number;
    highLevelHour1: number;
    groupCareHour1: number;
    directCareBeneficialHour1: number;
    midLevelBeneficialHour1: number;
    highLevelBeneficialHour1: number;
    groupCareBeneficialHour1: number;
    directCareCode1: string;
    midLevelCode1: string;
    highLevelCode1: string;
    groupCareCode1: string;

    isDraft2: boolean;
    serviceType2: string;
    startDate2: string;
    endDate2: string;
    directCareBeneficialHour2: number;
    midLevelBeneficialHour2: number;
    highLevelBeneficialHour2: number;
    groupCareBeneficialHour2: number;
    directCareHour2: number;
    midLevelHour2: number;
    highLevelHour2: number;
    groupCareHour2: number;
    directCareCode2: string;
    midLevelCode2: string;
    highLevelCode2: string;
    groupCareCode2: string;

    isDraft3: boolean;
    serviceType3: string;
    startDate3: string;
    endDate3: string;
    directCareBeneficialHour3: number;
    midLevelBeneficialHour3: number;
    highLevelBeneficialHour3: number;
    groupCareBeneficialHour3: number;
    directCareHour3: number;
    midLevelHour3: number;
    highLevelHour3: number;
    groupCareHour3: number;
    directCareCode3: string;
    midLevelCode3: string;
    highLevelCode3: string;
    groupCareCode3: string;

    isDraft4: boolean;
    serviceType4: string;
    startDate4: string;
    endDate4: string;
    directCareBeneficialHour4: number;
    midLevelBeneficialHour4: number;
    highLevelBeneficialHour4: number;
    groupCareBeneficialHour4: number;
    directCareHour4: number;
    midLevelHour4: number;
    highLevelHour4: number;
    groupCareHour4: number;
    directCareCode4: string;
    midLevelCode4: string;
    highLevelCode4: string;
    groupCareCode4: string;

    isDraft5: boolean;
    serviceType5: string;
    startDate5: string;
    endDate5: string;
    directCareBeneficialHour5: number;
    midLevelBeneficialHour5: number;
    highLevelBeneficialHour5: number;
    groupCareBeneficialHour5: number;
    directCareHour5: number;
    midLevelHour5: number;
    highLevelHour5: number;
    groupCareHour5: number;
    directCareCode5: string;
    midLevelCode5: string;
    highLevelCode5: string;
    groupCareCode5: string;

    isDraft6: boolean;
    serviceType6: string;
    startDate6: string;
    endDate6: string;
    directCareBeneficialHour6: number;
    midLevelBeneficialHour6: number;
    highLevelBeneficialHour6: number;
    groupCareBeneficialHour6: number;
    directCareHour6: number;
    midLevelHour6: number;
    highLevelHour6: number;
    groupCareHour6: number;
    directCareCode6: string;
    midLevelCode6: string;
    highLevelCode6: string;
    groupCareCode6: string;
    therapyTreatmentUnitsPer: string;
    therapyTreatmentUnitType: string;
    therapyTreatmentUnit: number;

    diagnosticEvaluationProcedureCode: string;
    therapyTreatmentProcedureCode: string,
    funderAuthorizationRecordType: string,
    funderAuthSpeciality: string,

    atRisk: boolean,
    dateAtRiskPublished: string,

    client: ClientProfile;
    latestSubmittedClientReport: ClientReportInfo;
    latestSubmittedClientReportByReportType: ClientReportInfo[] | null;
    authorizationItems: AuthorizationBillItem[] | null;

    hasAddendumSubmittedRecently: boolean;
    hasStaffingPlanSubmittedRecently: boolean;
    hasStaffingNonRecurringSubmittedConsecutively: boolean;

    billedMonths: string[];
    pendingAuthorizationsByClient: PendingAuthorizationInfo[] | null;
    draftAuthFromApprovedReportByClient: PendingAuthorizationInfo[] | null;

    // - functional fields only
    _authItemsAtRiskOfferedHoursStatus: string;
    _authItemsAtRiskNotificationDate: string;
    _atRiskAuthItemsAtRiskResponseDueDate: string;
}

export const getConcideredPendingAuthorization = (auth: AuthorizationInfo | undefined): PendingAuthorizationInfo[] => {
    if (!auth) return [];
    const pendingAuthorizations = auth.pendingAuthorizationsByClient || [];
    const draftAuthFromApprovedReport = auth.draftAuthFromApprovedReportByClient || [];
    
    return [...pendingAuthorizations, ...draftAuthFromApprovedReport];
}

export type PendingAuthorizationInfo = {
    guid: string;
    sfId: string;
    bhpnAuthNumber: string;
    startDate: string;
    endDate: string;
}

export const defaultServiceTypesByAuthTier1 = (auth: AuthorizationInfo) => {
    return auth.serviceType1?.split(';')
        .map(str => str.trim())
        .filter(str => !_.isEmpty(str))
        .sort()
        .map(st => serviceTypeInverse[st]) || [];
}

export const defaultClinicalRecommendationHoursFromAuth = (auth: AuthorizationInfo) => {
    return {
        caregiverMidLevelHoursMin: auth.careMediatedMidMinHours ?? auth.midLevelBeneficialHour1 ?? null,
        caregiverHighLevelHoursMin: auth.careMediatedHighMinHours ?? auth.highLevelBeneficialHour1 ?? null,
        caregiverMidLevelHoursMax: auth.careMediatedMidHours ?? auth.midLevelHour1 ?? null,
        caregiverHighLevelHoursMax: auth.careMediatedHighHours ?? auth.highLevelHour1 ?? null,
        practitionerParaprofessionalHoursMin: auth.practDirectBeneficialHours ?? auth.directCareBeneficialHour1 ?? null,
        practitionerParaprofessionalHoursMax: auth.practDirectOptimalHours ?? auth.directCareHour1 ?? null,
        practitionerMidLevelHoursMin: auth.practMidBeneficialHours ?? auth.midLevelBeneficialHour1 ?? null,
        practitionerMidLevelHoursMax: auth.practMidOptimalHours ?? auth.midLevelHour1 ?? null,
        practitionerHighLevelHoursMin: auth.practHighBeneficialHours ?? auth.highLevelBeneficialHour1 ?? null,
        practitionerHighLevelHoursMax: auth.practHighOptimalHours ?? auth.highLevelHour1 ?? null,
        practitionerGroupCareHoursMin: auth.practGroupBeneficialHours ?? auth.groupCareBeneficialHour1 ?? null,
        practitionerGroupCareHoursMax: auth.practGroupOptimalHours ?? auth.groupCareHour1 ?? null,
    };
}

export enum AuthClosedReason {
    Authorization_Period_Closed = "Authorization Period Closed",
    Addendum_Report_Submitted = "Addendum Report Submitted",
    Treatment_Terminated_Early = "Treatment Terminated Early",
    Treatment_Limit_Reached = "Treatment Limit Reached",
    Treatment_Terminated = "Treatment Terminated",
    Reached_End_Date = "Reached End Date",
    Client_discharged = "Client discharged",
    Created_in_error = "Created in error",
    Client_Transfer = "Client Transfer",
    Coverage_Termination = "Coverage Termination"
}

export const {
    nameToValue: AuthClosedReasonMap
} = EnumToMapping(AuthClosedReason)

export type AuthorizationTierDetail = {
    tier: number;
    isDraft: boolean;
    directCareHour: string;
    directCareCode: string;
    groupCareHour: string;
    groupCareCode: string;
    midLevelHour: string;
    midLevelCode: string;
    highLevelHour: string;
    highLevelCode: string;
    startDate: string;
    endDate: string;
    directCareBeneficialHour: string;
    midLevelBeneficialHour: string;
    highLevelBeneficialHour: string;
    groupCareBeneficialHour: string;
    serviceType: string;
}

export const extractAvailableServiceTypesFromTierDetail = (
    authorization: AuthorizationInfo,
    tierNum: number
) => {
    const serviceTypes = authorization[`serviceType${tierNum}` as AuthorizationInfoKey] as string;
    const isDraft = authorization[`isDraft${tierNum}` as AuthorizationInfoKey] as boolean;
    if (isDraft) return "";
    return serviceTypes;
}

export const extractAuthorizationTierDetail = (
    authorization: AuthorizationInfo,
    tierNum: number
): AuthorizationTierDetail => {
    let [
        tier,
        isDraft,
        directCareHour,
        directCareCode,
        groupCareHour,
        groupCareCode,
        midLevelHour,
        midLevelCode,
        highLevelHour,
        highLevelCode,
        startDate,
        endDate,
        directCareBeneficialHour,
        midLevelBeneficialHour,
        highLevelBeneficialHour,
        groupCareBeneficialHour,
        serviceType,
    ]
        = [tierNum, false, "None", "None", "None", "None", "None", "None", "None", "None", "None", "None", "None", "None", "None", "None", "None"];
    [
        "isDraft",
        "directCareHour",
        "directCareCode",
        "groupCareHour",
        "groupCareCode",
        "midLevelHour",
        "midLevelCode",
        "highLevelHour",
        "highLevelCode",
        "directCareBeneficialHour",
        "midLevelBeneficialHour",
        "highLevelBeneficialHour",
        "groupCareBeneficialHour",
        "startDate",
        "endDate",
        "serviceType"
    ].forEach((field) => {
        if (authorization[`${field}${tierNum}` as AuthorizationInfoKey] != null) {
            // @ts-ignore
            const value = authorization[`${field}${tierNum}` as AuthorizationInfoKey].toString();
            switch (field) {
                case "isDraft":
                    isDraft = value === 'true';
                    break;
                case "directCareBeneficialHour":
                    directCareBeneficialHour = value;
                    break;
                case "midLevelBeneficialHour":
                    midLevelBeneficialHour = value;
                    break;
                case "highLevelBeneficialHour":
                    highLevelBeneficialHour = value;
                    break;
                case "groupCareBeneficialHour":
                    groupCareBeneficialHour = value;
                    break;
                case "directCareHour":
                    directCareHour = value;
                    break;
                case "directCareCode":
                    directCareCode = value
                    break;
                case "groupCareHour":
                    groupCareHour = value
                    break;
                case "groupCareCode":
                    groupCareCode = value
                    break;
                case "midLevelHour":
                    midLevelHour = value
                    break;
                case "midLevelCode":
                    midLevelCode = value
                    break;
                case "highLevelHour":
                    highLevelHour = value
                    break;
                case "highLevelCode":
                    highLevelCode = value
                    break;
                case "startDate":
                    startDate = value
                    break;
                case "endDate":
                    endDate = value;
                    break;
                case "serviceType":
                    serviceType = value;
                    break;
            }
        }
    })
    return {
        tier,
        isDraft,
        directCareHour,
        directCareCode,
        groupCareHour,
        groupCareCode,
        midLevelHour,
        midLevelCode,
        highLevelHour,
        highLevelCode,
        startDate,
        endDate,
        directCareBeneficialHour,
        midLevelBeneficialHour,
        highLevelBeneficialHour,
        groupCareBeneficialHour,
        serviceType,
    };
}

export type AuthorizationInfoKey = keyof AuthorizationInfo;

export type ServiceTypeSelection = {
    options: Map<string, string>,
    serviceTypes: string[],
}

export const serviceTypeOptionsAvailableForAllTiers = (auth: AuthorizationInfo): ServiceTypeSelection | null => {
    const tiers = [...Array(auth.numOfTier + 1).keys()].slice(1);
    let validatedServiceTypes: string[] = [];
    tiers.forEach((tier) => {
        validatedServiceTypes.push(extractAvailableServiceTypesFromTierDetail(auth, tier));
    })


    const filtered = validatedServiceTypes
        .filter((st) => st !== "" && st !== null && st !== undefined)
        .map((st) => st.split(SPLIT_DELIMITER).sort());

    const options = filtered.map((sts) =>
        [sts.join(SPLIT_DELIMITER),
            sts.map(st => ReportServiceTypeValueToKey[st]).join(SPLIT_DELIMITER)]
    );

    if (options.length === 0) return null;

    return {
        options: new Map(options.map((item) => [item[1], item[0]])),
        serviceTypes: [...new Set(options.map((item) => item[1].split(SPLIT_DELIMITER)).flat())]
    };
}

export type ClientReportInfo = {
    guid: string;
    clientProfile: ClientInfo;
    authorizationNumber: string;
    reportType: string;
    firstTreatmentOfferedDate: string;
    lastDayOfServiceBilled: string;
    newAuthorizationStartDate: string;
    finalDiagnosticContactDate: string;
    vinelandIncluded: string;

    authorizedDate: string;
    authorizedBy: string;

    sfSyncStatus: string;

    reportDueDay: string;
    reportStatus: string;
    reportSubmittedBy: string;
    reportApproved: string;
    reportApprovedBy: string;
    newAuthorization: string;

    serviceRecommended: string;
    serviceType: string[];
    caregiverMidLevelHoursMin: number;
    caregiverMidLevelHoursMax: number;
    caregiverHighLevelHoursMin: number;
    caregiverHighLevelHoursMax: number;
    practitionerParaprofessionalHoursMin: number;
    practitionerParaprofessionalHoursMax: number;
    practitionerMidLevelHoursMin: number;
    practitionerMidLevelHoursMax: number;
    practitionerHighLevelHoursMin: number;
    practitionerHighLevelHoursMax: number;
    practitionerGroupCareHoursMin: number;
    practitionerGroupCareHoursMax: number;
    offeredParaprofessionalHours: number;
    offeredMidLevelHours: number;
    offeredHighLevelHours: number;
    offeredGroupCareHours: number;
    treatmentOffered: string;

    clinicalRationale: string;
    nameAndTitle: string;
    licenseNumber: string;

    authorization: AuthorizationClientReportInfo;

    clientReportFiles: ClientReportFile[];
    clientReportFilesBySfOperationCase: ClientReportFile[];

    submittedBy: Person;
    clientReportNotifyUsers: Person[];
    sfOperationCaseNumber: string;
    sfOperationCaseId: string;
    sfOperationCaseRptStatus: string;
    sfCloseReason: string;
    sfCloseDate: string;
    sfLastModifiedDate: string;

    submittedAt: string;

    shared?: boolean;
}

export type AuthorizationClientReportInfo = {
    bhpnAuthNumber: string;
    reportDueDate: string;
    reportSubmittedDate: string
}

export type PractitionerCareTeamInput = {
    memberRole: string;
    startDate: string;
    endDate: string | undefined;
    clinicianGuid: string;
}

export type ClientReportSubmitClinicalRecommendationHourInput = {
    caregiverMidLevelHoursMin: number;
    caregiverMidLevelHoursMax: number;
    caregiverHighLevelHoursMin: number;
    caregiverHighLevelHoursMax: number;
    practitionerParaprofessionalHoursMin: number;
    practitionerParaprofessionalHoursMax: number;
    practitionerMidLevelHoursMin: number;
    practitionerMidLevelHoursMax: number;
    practitionerHighLevelHoursMin: number;
    practitionerHighLevelHoursMax: number;
    practitionerGroupCareHoursMin: number;
    practitionerGroupCareHoursMax: number;
};

export type ClientReportSubmitClinicalRecommendationInput = {
    serviceType: string[];
    serviceRecommended: string;
} & ClientReportSubmitClinicalRecommendationHourInput;

export type ClientReportSubmitClientOfferHourInput = {
    currentOfferedParaprofessionalHours: number;
    currentOfferedMidLevelHours: number;
    currentOfferedHighLevelHours: number;
    currentOfferedGroupCareHours: number;
    offeredParaprofessionalHours: number | null;
    offeredMidLevelHours: number | null;
    offeredHighLevelHours: number | null;
    offeredGroupCareHours: number | null;
};

export type ClientReportSubmitCommentInput = {
    comments: string;
    clinicalRationale: string;
};


export type ClientTransferringSubmitInput = {
    serviceType: string[];
    offeredStartDate: string;
    offeredParaprofessionalHours: number | null;
    offeredMidLevelHours: number | null;
    offeredHighLevelHours: number | null;
    offeredGroupCareHours: number | null;
};

export type ClientReportSubmitInput =
    {
        clientGuid: string;
        reportType: string;
        firstTreatmentOfferedDate: string | null;
        lastDayOfServiceBilled: string;
        newAuthorizationStartDate: string;
        finalDiagnosticContactDate: string;
        vinelandIncluded: string;
        anticipatedDischargeDate: string;
        authorizationGuid: string;
        nameAndTitle: string;
        licenseNumber: string;
        shouldCreateNewOperationCase: string;
        recommendationDifferenceRationale?: string;
    }
    & ClientReportSubmitCommentInput
    & ClientReportSubmitClinicalRecommendationInput
    & ClientReportSubmitClientOfferHourInput
    & ClientReportSubmitTreatmentOfferedInput
    & ClientReportAddendumRequestReasonInput;

export type ClientReportFile = {
    guid: string;
    fileName: string;
    fileExtension: string;
    downloadUrl: string;
}

export type ClientDocument = {
    guid: string;
    fileName: string;
    documentName: string;
    documentType: string;
    documentDescription: string;
    documentDate: string;
    documentLink: string;
}

export type ConsultWithCCMInput = {
    consultWithCCM: string;
    stepsTaken: string;
};

export type ClientFamilyConcernInput = {
    familyConcern: string;
    attemptToResolve: string;
    familyResponse: string;
    requestedSupport: string;
};

export type FsoInquiryByCurrentProviderInput = {
    inquiry: string;
}

export type FsoSupportInput = {
    relatedToBilling: YesNoOption | null;
    confirmedWithBilling?: YesNoOption | null;
    billingRejectionsCorrected?: YesNoOption | null;
    fsoInquiry: string;
}

export type AnticipatedDischargeDateUpdateInput = {
    submittedAnticipatedDischargeDate?: string;
    newAnticipatedDischargeDateDefined: string;
    clinicalRationale: string;
    dateDischargeDiscussed?: string;
    reasonForDischarge: string;
    dischargeInitiator: string;
    clientFamilyAgreesWithDischarge: string;
    reasonForDisagreement?: string;
    dateOfLastPlannedSession?: string;
    dischargeReportAcknowledgement: boolean;
}

export type AuthReviewNeededInput = {
    authReviewFromDate: string;
    authReviewToDate: string;
    requestedExtensionDate: string | null;
    rationaleForDelay: string;
    firstScheduledAssessment: string | null;
    authorizationReviewReasons: string;
    comments: string;
};

export type MissingDocumentationInput = {
    missingDocumentations: string;
    otherDocumentations: string;
};

export type InsuranceNotificationInput = {
    insuranceInactiveDate: string;
    additionalInformation: string;
};

export type ReportStatusInquiryInput = {
    reportType: string;
    reportStatusInquiry: string; // SPLIT_DELIMITER for list
    additionalDetails: string;
};

export type NoContactInput = {
    initialDateOfContactAttempt: string;
    dateContactResumed: string;
    dateServiceExpectedToResumed: string;
    lastContactAttemptDate: string;
    numberOfContactAttempts: string;
    initialTypeOfContact: string;
    typeOfContact: string;
    providerWasAbleToContactClient: string;
    comments: string;
}

export type NotAnActiveClientInput = {
    wasClientAssignedToProvider: string;
    lastServiceDate: string;
    comments: string;
}

export type StaffingFormInput = {
    comments: string;
    fullOfferStartDate: string;
    reviewedAndApprovedByClientFamilyDate: string;
} & ClientReportSubmitClientOfferHourInput & Omit<ClientReportSubmitClinicalRecommendationInput, 'serviceRecommended'>;

export type ClientReportSubmitTreatmentOfferedInput = {
    treatmentOffered: string;
}

export type ClientReportAddendumRequestReasonInput = {
    addendumRequestReason: string[];
}

export type StaffingPlan = {
    guid: string;
} & StaffingFormInput;

export type AuthorizationBillItem = {
    guid: string;

    offeredHoursStatus: string;
    status: string;
    startDate: string;
    endDate: string;
    closedReason: string;
    procedureCode: string;
    modifier: string;
    practitionerRole: string;
    tier: number;
    minimumHoursAuthorized: number;
    maximumHoursAuthorized: number;
    hoursBilled: number;
    directCareHoursRequired: number;
    dateAtRiskPublished: string;
    atRiskResponseDueDate: string;
    staffingAddendumDueDate: string;
    staffingAddendumSubmittedDate: string;
    atRiskResponseDate: string;
    atRiskResponseReasons: string[] | null;
    lastModifiedDate: string;
    lastModifiedBy: string;
    submittedAuthReviewedDate: string;

    chargeEntries: AuthorizationBillItemChargeEntry[] | null;

    staffingPlan: StaffingPlan | null;

    submitter: Person | null,

    // function only
    _notificationDate: string;
    _lastBillingDate: string;
    _unitsToMeetFullOffer: string;
}

export type SubmitAtRiskReasonsInput = {
    authItemGuids: string[];
    reasons: string[];
    staffingPlan: StaffingFormInput | null;
    staffingAction: StaffingAction | null;
    addendumReportGuid: string | null;
    treatmentHold: TreatmentHoldSubmitInput | null;
    notAnActiveClient: NotAnActiveClientSubmitInput | null;
    clientTransferring: ClientTransferringSubmitInput | null;
    noContact: NoContactInput | null;
    authReviewNeeded: AuthReviewNeededInput | null;
    updateAuthItemOnly: boolean;
}

export type NotAnActiveClientSubmitInput = {
    description: string | null;
    lastServiceDate: string | null;
}

export enum ContactType {
    Email = 'Email',
    myBrightlink = 'myBrightlink',
    Phone_Call = 'Phone Call',
    Text = 'Text',
}

export const {
    nameToValue: ContactTypeMap,
    nameToValueWithDefault: ContactTypeWithDefault
} = EnumToMapping(ContactType, {label: "All", value: ""});

export enum AuthorizationReviewReason {
    CheckingOnStatusOfAuthorization = 'Checking on Status of Authorization',
    IncorrectAuthorizedHours = 'Incorrect authorized hours',
    IncorrectServiceType = 'Incorrect service type',
    IncorrectProcedureCode = 'Incorrect procedure code(s)',
    Other = 'Other',
    RequestingAuthorizationExtension = 'Requesting Authorization Extension'
}

export const {
    nameToValue: AuthorizationReviewReasonMap,
    nameToValueWithDefault: AuthorizationReviewReasonWithDefault
} = EnumToMapping(AuthorizationReviewReason, {label: "All", value: ""});

export enum MissingDocumentType {
    BhtInitialAssessment =  'BHT Initial Assessment',
    ClientPacket = 'Client Packet',
    ClinicalPacket = 'Clinical Packet',
    Other = 'Other',
    RecentReport = 'Recent Report',
    VinelandAssessment = 'Vineland Assessment'
}

export const {
    nameToValue: MissingDocumentTypeMap,
} = EnumToMapping(MissingDocumentType, {label: "All", value: ""});

export enum OfferedHoursStatus {
    At_Risk = "At Risk",
    Good_Standing = "Good Standing",
}

export const {
    nameToValue: OfferedHoursStatusMap,
    nameToValueWithDefault: OfferedHoursStatusMapWithDefault
} = EnumToMapping(OfferedHoursStatus, {label: "All", value: ""});

export enum ProcedureCode {
    H0004 = "H0004",
    H0032 = "H0032",
    H2014 = "H2014",
    H2019 = "H2019",
}

export const {
    nameToValue: ProcedureCodeMap,
    nameToValueWithDefault: ProcedureCodeWithDefaultValue
} = EnumToMapping(ProcedureCode, {label: "All", value: ""});

export enum TreatmentHoldInitiator {
    Client_Family = "Client Family",
    Provider = "Provider",
    Catalight_Care_Services = "Catalight Care Services",
}

export const {nameToValue: TreatmentHoldInitiatorMap} = EnumToMapping(TreatmentHoldInitiator);

export enum TreatmentHoldRequestReason {
    Thirty_Day_Vacation = "Vacation",
    Medical_Condition = "Medical Condition",
    Family_Transition = "Family Transition",

    Other = "Other",
}

export const {nameToValue: TreatmentHoldRequestReasonMap} = EnumToMapping(TreatmentHoldRequestReason);

export enum TreatmentHoldProviderResumeServices {
    Yes = "Yes",
    No = "No"
}
export const {nameToValue: TreatmentHoldProviderResumeServicesMap} = EnumToMapping(TreatmentHoldProviderResumeServices);

export enum VinelandIncludedOptions {
    Yes = "Yes",
    No = "No"
}
export const {nameToValue: VinelandIncludedOptionsMap} = EnumToMapping(VinelandIncludedOptions);

export enum AtRiskReason {
    Full_offer_Client_Declined = "Full offer - Client Declined",
    Full_offer_Billing_Pending = "Full offer - Billing Pending",
    Auth_Split_Hours_Provided = "Auth Split - Hours Provided",
    Auth_Review_Needed = "Auth Review Needed",
    Client_Discharged = "Client Discharged",
    Staffing = "Staffing",
    Staffing_Non_recurring = "Staffing-Non recurring",
    Staffing_Plan_recently_submitted = "Staffing Plan recently submitted",
    Addendum_recently_submitted = "Addendum recently submitted",
    Change_Hour_Addendum = "Change Hour Addendum",
    Full_Addendum = "Full Addendum",
    Treatment_Hold = "Treatment Hold",
    No_Contact = "No Contact",
    Not_an_Active_Client = "Not an Active Client",
    Client_is_transferring = "Client is transferring",
    Switched_to_Caregiver_Mediated_Flex_Tx = "Switched to Caregiver Mediated - Flex. Tx",
    Consult_With_CCM = "Consult with CCM"
}

export const {
    names: AtRiskReasonNames,
    nameToValueWithDefault: AtRiskReasonMapWithDefaultValue,
    nameToValue: AtRiskReasonMap,
    valueToName: AtRiskReasonValueToName,
} = EnumToMapping(AtRiskReason, {label: "All At Risk Response", value: ""})

export enum AtRiskResponseStatus {
    No_Response = "No Response",
    Partial_Response = "Partial Response"
}

export const {
    nameToValue: AtRiskResponseStatusMap,
    nameToValueWithDefault: AtRiskResponseStatusMapWithDefaultValue,
} = EnumToMapping(AtRiskResponseStatus, {label: "All Status", value: ""})

export enum ShortAddendumRequestReason {
    Request_For_Increase_In_Hours = "Request for increase in hours",
    Request_For_Decrease_In_Hours = "Request for decrease in hours",
}

export const {
    nameToValue: ShortAddendumRequestReasonMap,
} = EnumToMapping(ShortAddendumRequestReason);

export enum AddendumRequestReason {
    Change_In_Service_Line = "Change in service line",
    Change_In_Location_Of_Services = "Change in location of services",
    Dangerous_Behaviors = "Dangerous behaviors",
    New_Treatment_Goals = "New treatment goals",
    Request_For_Increase_In_Hours = "Request for increase in hours",
    Request_For_Decrease_In_Hours = "Request for decrease in hours",
}

export const {
    nameToValue: AddendumRequestReasonMap,
} = EnumToMapping(AddendumRequestReason);


export type AuthorizationBillItemChargeEntry = {
    guid: string;
    serviceDate: string;
    procedureCode: string;
    hoursBilled: number;
    practitionerName: string;
    submissionDate: string;
}

export enum FunderAuthorizationRecordType {
    Physical_Therapy = "Physical Therapy",
    Occupational_Therapy = "Occupational Therapy",
    Speech_Therapy = "Speech Therapy",
    Speech_Therapy_Group = "Speech Therapy - Group"
}

export const {
    nameToValueWithDefault: FunderAuthorizationRecordTypeMap,
} = EnumToMapping(FunderAuthorizationRecordType, {value: "", label: "All Funder Authorization Type"})

export enum FunderAuthorizationSpeciality {
    Physical_Therapy = "Physical Therapy",
    Occupational_Therapy = "Occupational Therapy",
    Speech_Therapy = "Speech Therapy",
}

export const {
    nameToValue: FunderAuthorizationSpecialityMap,
} = EnumToMapping(FunderAuthorizationRecordType, {value: "", label: "All Funder Authorization Type"})

export enum DiagnosticEvaluationType {
    Diagnostic_Evaluation = "Diagnostic Evaluation"
}

export enum AuthorizationRecordType {
    ABA_Assessment_Authorization = "ABA Assessment Authorization",
    ABA_Treatment_Authorization = "ABA Treatment Authorization",
    ABA_Treatment_Authorization_Group_Care = "ABA Treatment Authorization - Group Care",
    Diagnostic_Evaluation_Authorization = "Diagnostic Evaluation Authorization",
    ABA_Transfer_Reevaluation = "Transfer Reevaluations",
    Therapy_Assessment_Authorization = "Therapy Assessment Authorization",
    Therapy_Treatment_Authorization = "Therapy Treatment Authorization",
    Psychotherapy_Assessment = "Psychotherapy Assessment",
    Psychotherapy_Treatment = "Psychotherapy Treatment"
}

export const {
    valueToName: AuthorizationRecordTypeValueToName,
    nameToValueWithDefault: AuthorizationRecordTypeMap,
} = EnumToMapping(AuthorizationRecordType, {value: "", label: "All Authorization Type"})


export enum AuthorizationType {
    ABA_Assessment_Authorization = "ABA Assessment Authorization",
    ABA_Treatment_Authorization = "ABA Treatment Authorization",
    ABA_Treatment_Authorization_Group_Care = "ABA Treatment Authorization - Group Care",
    Diagnostic_Evaluation_Authorization = "Diagnostic Evaluation Authorization",
    Occupational_Therapy = "Occupational Therapy",
    Physical_Therapy = "Physical Therapy",
    Psychotherapy_Assessment = "Psychotherapy Assessment",
    Psychotherapy_Treatment = "Psychotherapy Treatment",
    Speech_Therapy = "Speech Therapy",
    Speech_Therapy_Group = "Speech Therapy - Group",
    ABA_Transfer_Reevaluation = "Transfer Reevaluations"
}

export const {
    nameToValueWithDefault: AuthorizationTypeMap,
} = EnumToMapping(AuthorizationType, {value: "", label: "All Authorization Type"}, true)


export enum AssessmentAuthClientReportType {
    ABA_ASSESSMENT_REPORT = 'ABA Assessment Report',
    ABA_ADDENDUM_REPORT = 'ABA Addendum Report',
    ABA_DISCHARGE_REPORT = 'ABA Discharge Report',
    ABA_TRANSFER_REPORT = 'ABA Transfer Report',
}

export const {
    nameToValue: AssessmentAuthClientReportTypeMap,
} = EnumToMapping(AssessmentAuthClientReportType, {value: "", label: "All Report Type"})

enum TreatmentAuthChatReportType {
    CHAT_PROGRESS_REPORT = 'Chat Progress Report',
    CHAT_TRANSFER_REPORT = 'Chat Transfer Report',
    CHAT_ADDENDUM_REPORT = 'Chat Addendum Report',
    CHAT_DISCHARGE_REPORT = 'Chat Discharge Report'
}

const {
    nameToValue: TreatmentAuthChatReportTypeMap,
} = EnumToMapping(TreatmentAuthChatReportType)


enum TreatmentAuthConnectReportType {
    CONNECT_PROGRESS_REPORT = 'Connect Progress Report',
    CONNECT_TRANSFER_REPORT = 'Connect Transfer Report',
    CONNECT_ADDENDUM_REPORT = 'Connect Addendum Report',
    CONNECT_DISCHARGE_REPORT = 'Connect Discharge Report',
}

const {
    nameToValue: TreatmentAuthConnectReportTypeMap,
} = EnumToMapping(TreatmentAuthConnectReportType)


enum TreatmentAuthCompassReportType {
    COMPASS_BEHAVIORAL_ASSESSMENT_REPORT = 'Compass Behavioral Assessment Report',
    COMPASS_BEHAVIORAL_PROGRESS_REPORT = 'Compass Behavioral Progress Report',
    COMPASS_BEHAVIORAL_TRANSFER_REPORT = 'Compass Behavioral Transfer Report',
    COMPASS_BEHAVIORAL_ADDENDUM_REPORT = 'Compass Behavioral Addendum Report',
    COMPASS_BEHAVIORAL_DISCHARGE_REPORT = 'Compass Behavioral Discharge Report',
}

const {
    nameToValue: TreatmentAuthCompassReportTypeMap,
} = EnumToMapping(TreatmentAuthCompassReportType)

enum TreatmentAuthRUBIReportType {
    RUBI_ASSESSMENT_REPORT = 'RUBI Assessment Report',
    RUBI_PROGRESS_REPORT = 'RUBI Progress Report',
    RUBI_TRANSFER_REPORT = 'RUBI Transfer Report',
    RUBI_ADDENDUM_REPORT = 'RUBI Addendum Report',
    RUBI_DISCHARGE_REPORT = 'RUBI Discharge Report',
}

const {
    nameToValue: TreatmentAuthRUBIReportTypeMap,
} = EnumToMapping(TreatmentAuthRUBIReportType)

enum TreatmentAuthCalibreReportType {
    CALIBRATE_ASSESSMENT_REPORT = 'Calibrate Assessment Report',
    CALIBRATE_PROGRESS_REPORT = 'Calibrate Progress Report',
    CALIBRATE_TRANSFER_REPORT = 'Calibrate Transfer Report',
    CALIBRATE_ADDENDUM_REPORT = 'Calibrate Addendum Report',
    CALIBRATE_DISCHARGE_REPORT = 'Calibrate Discharge Report',
}

const {
    nameToValue: TreatmentAuthCalibreReportTypeMap,
} = EnumToMapping(TreatmentAuthCalibreReportType)

export enum TreatmentAuthClientReportType {
    ABA_PROGRESS_REPORT = 'ABA Progress Report',
    ABA_ADDENDUM_REPORT = 'ABA Addendum Report',
    ABA_DISCHARGE_REPORT = 'ABA Discharge Report',
    ABA_TRANSFER_REPORT = 'ABA Transfer Report',
}

export const {
    nameToValue: TreatmentAuthClientReportTypeMap,
} = EnumToMapping(TreatmentAuthClientReportType, {value: "", label: "All Report Type"})

export enum TreatmentAuthSpeechTherapyReportType {
    SPEECH_THERAPY_PROGRESS_REPORT = 'Speech Therapy Progress Report',
    SPEECH_THERAPY_TRANSFER_REPORT = 'Speech Therapy Transfer Report',
    SPEECH_THERAPY_DISCHARGE_REPORT = 'Speech Therapy Discharge Report',
}
export const {
    nameToValue: TreatmentAuthSpeechTherapyReportTypeMap,
} = EnumToMapping(TreatmentAuthSpeechTherapyReportType)

export enum TreatmentAuthOccupationalTherapyReportType {
    OCCUPATIONAL_THERAPY_PROGRESS_REPORT = 'Occupational Therapy Progress Report',
    OCCUPATIONAL_THERAPY_TRANSFER_REPORT = 'Occupational Therapy Transfer Report',
    OCCUPATIONAL_THERAPY_DISCHARGE_REPORT = 'Occupational Therapy Discharge Report',
}
export const {
    nameToValue: TreatmentAuthOccupationalTherapyReportTypeMap,
} = EnumToMapping(TreatmentAuthOccupationalTherapyReportType)

export enum TreatmentAuthPhysicalTherapyReportType {
    PHYSICAL_THERAPY_PROGRESS_REPORT = 'Physical Therapy Progress Report',
    PHYSICAL_THERAPY_TRANSFER_REPORT = 'Physical Therapy Transfer Report',
    PHYSICAL_THERAPY_DISCHARGE_REPORT = 'Physical Therapy Discharge Report',
}
export const {
    nameToValue: TreatmentAuthPhysicalTherapyReportTypeMap,
} = EnumToMapping(TreatmentAuthPhysicalTherapyReportType)

export enum TransferReevaluationAuthClientReportType {
    ABA_TRANSFER_REEVALUATION_REPORT = 'ABA Transfer Re-evaluation Report',
}

export const {
    nameToValue: TransferReevaluationAuthClientReportTypeMap,
    valueToName: TransferReevaluationAuthClientReportTypeFromValueMap
} = EnumToMapping(TransferReevaluationAuthClientReportType, {value: "", label: "All Report Type"})

export enum ClientReportReportType {
    ABA_SHORT_ADDENDUM = 'Change Hours Addendum',
    ABA_ADDENDUM_REPORT = 'ABA Addendum Report',
    ABA_ASSESSMENT_REPORT = 'ABA Assessment Report',
    ABA_PROGRESS_REPORT = 'ABA Progress Report',
    ABA_DISCHARGE_REPORT = 'ABA Discharge Report',
    ABA_TRANSFER_REPORT = 'ABA Transfer Report',
    ABA_TRANSFER_REEVALUATION_REPORT = 'ABA Transfer Re-evaluation Report',
    CHANGE_HOURS_ADDENDUM = 'Change Hours Addendum',
    CHAT_ASSESSMENT_REPORT = 'Chat Assessment Report',
    CONNECT_ASSESSMENT_REPORT = 'Connect Assessment Report',
    RUBI_ASSESSMENT_REPORT = 'RUBI Assessment Report',
    COMPASS_BEHAVIORAL_ASSESSMENT_REPORT = 'Compass Behavioral Assessment Report',
    CALIBRATE_ASSESSMENT_REPORT = 'Calibrate Assessment Report',
    PSYCHOTHERAPY_ASSESSMENT_REPORT = 'Psychotherapy Assessment Report',
    CHAT_PROGRESS_REPORT = 'Chat Progress Report',
    CHAT_TRANSFER_REPORT = 'Chat Transfer Report',
    CHAT_ADDENDUM_REPORT = 'Chat Addendum Report',
    CHAT_DISCHARGE_REPORT = 'Chat Discharge Report',
    CONNECT_PROGRESS_REPORT = 'Connect Progress Report',
    CONNECT_TRANSFER_REPORT = 'Connect Transfer Report',
    CONNECT_ADDENDUM_REPORT = 'Connect Addendum Report',
    CONNECT_DISCHARGE_REPORT = 'Connect Discharge Report',
    COMPASS_BEHAVIORAL_PROGRESS_REPORT = 'Compass Behavioral Progress Report',
    COMPASS_BEHAVIORAL_TRANSFER_REPORT = 'Compass Behavioral Transfer Report',
    COMPASS_BEHAVIORAL_ADDENDUM_REPORT = 'Compass Behavioral Addendum Report',
    COMPASS_BEHAVIORAL_DISCHARGE_REPORT = 'Compass Behavioral Discharge Report',
    RUBI_PROGRESS_REPORT = 'RUBI Progress Report',
    RUBI_TRANSFER_REPORT = 'RUBI Transfer Report',
    RUBI_ADDENDUM_REPORT = 'RUBI Addendum Report',
    RUBI_DISCHARGE_REPORT = 'RUBI Discharge Report',
    CALIBRATE_PROGRESS_REPORT = 'Calibrate Progress Report',
    CALIBRATE_TRANSFER_REPORT = 'Calibrate Transfer Report',
    CALIBRATE_ADDENDUM_REPORT = 'Calibrate Addendum Report',
    CALIBRATE_DISCHARGE_REPORT = 'Calibrate Discharge Report',
    PSYCHOTHERAPY_PROGRESS_REPORT = 'Psychotherapy Progress Report',
    COMPASS_PSYCHOTHERAPY_PROGRESS_REPORT = 'Compass Psychotherapy Progress Report',
    DIAGNOSTIC_EVALUATION_REPORT = 'Diagnostic Evaluation Report',
    DIAGNOSTIC_EVALUATION_AFTER_VISIT_REPORT = 'Diagnostic Evaluation After Visit Summary',
    SPEECH_THERAPY_PROGRESS_REPORT = 'Speech Therapy Progress Report',
    SPEECH_THERAPY_TRANSFER_REPORT = 'Speech Therapy Transfer Report',
    SPEECH_THERAPY_DISCHARGE_REPORT = 'Speech Therapy Discharge Report',
    OCCUPATIONAL_THERAPY_PROGRESS_REPORT = 'Occupational Therapy Progress Report',
    OCCUPATIONAL_THERAPY_TRANSFER_REPORT = 'Occupational Therapy Transfer Report',
    OCCUPATIONAL_THERAPY_DISCHARGE_REPORT = 'Occupational Therapy Discharge Report',
    PHYSICAL_THERAPY_PROGRESS_REPORT = 'Physical Therapy Progress Report',
    PHYSICAL_THERAPY_TRANSFER_REPORT = 'Physical Therapy Transfer Report',
    PHYSICAL_THERAPY_DISCHARGE_REPORT = 'Physical Therapy Discharge Report',
}

export const {
    nameToValue: ClientReportReportTypeMap,
    nameToValueWithDefault: ClientReportReportTypeMapWithDefault,
    valueToName: ClientReportReportTypeFromValueMap
} = EnumToMapping(ClientReportReportType, {value: "", label: "All Report Type"}, true)

const isAbaTreatmentOrGroupAuth = (auth: AuthorizationInfo | undefined) =>
    AuthorizationRecordType.ABA_Treatment_Authorization === AuthorizationRecordTypeMap.get(auth?.recordType ?? "") ||
    AuthorizationRecordType.ABA_Treatment_Authorization_Group_Care === AuthorizationRecordTypeMap.get(auth?.recordType ?? "");

export const isTransferReevaluationAuth = (auth: AuthorizationInfo | undefined) =>
    AuthorizationRecordType.ABA_Transfer_Reevaluation === AuthorizationRecordTypeMap.get(auth?.recordType ?? "");

const isAbaAssessmentAuth = (auth: AuthorizationInfo | undefined) =>
    AuthorizationRecordType.ABA_Assessment_Authorization === AuthorizationRecordTypeMap.get(auth?.recordType ?? "")

export const isAbaAuthorization = (auth: AuthorizationInfo | undefined) => isAbaTreatmentOrGroupAuth(auth) || isAbaAssessmentAuth(auth);

export const isTreatmentAuth = (auth: AuthorizationInfo | undefined): boolean => {
    if (!auth) return false;
    switch (AuthorizationRecordTypeMap.get(auth.recordType)) {
        case AuthorizationRecordType.ABA_Treatment_Authorization:
        case AuthorizationRecordType.Psychotherapy_Treatment:
        case AuthorizationRecordType.Therapy_Treatment_Authorization:
        case AuthorizationRecordType.ABA_Treatment_Authorization_Group_Care:
            return true;
        default:
            return false;
    }
}

export const isAssessmentAuth = (auth: AuthorizationInfo | undefined): boolean => {
    if (!auth) return false;
    switch (AuthorizationRecordTypeMap.get(auth.recordType)) {
        case AuthorizationRecordType.ABA_Assessment_Authorization:
        case AuthorizationRecordType.Therapy_Assessment_Authorization:
        case AuthorizationRecordType.Psychotherapy_Assessment:
            return true;
        default:
            return false;
    }
}

export const clientReportTypeByAuthRecordType = (auth: AuthorizationInfo | undefined) => {
    if (!auth) return ClientReportReportTypeMap;
    const allServiceTypes = serviceTypeOptionsAvailableForAllTiers(auth)?.serviceTypes ?? null;
    return reportTypesByAuthInfo(auth.recordType, auth.assessmentType, auth.funderAuthSpeciality, allServiceTypes ?? []);
}

export enum PractitionerCareTeamRole {
    HIGH_LEVEL_SUPERVISOR = "High Level Supervisor",
    MID_LEVEL_SUPERVISOR = "Mid Level Supervisor",
}

export const {
    nameToValue: PractitionerCareTeamRoleMap
} = EnumToMapping(PractitionerCareTeamRole)

export enum CaseManagerCareTeamRole {
    CATALIGHT_CASE_MANAGER = "Catalight Case Manager"
}

export const TeamContactRole = {
    ...CaseManagerCareTeamRole,
    ...PractitionerCareTeamRole
}

export const {
    names: TeamContactRoleNames,
    nameToValue: TeamContactRoleMap,
} = EnumToMapping(TeamContactRole)

export enum ClientReportReportStatus {
    REVIEW_IN_PROGRESS = "Review in Progress",
    RETURNED_TO_PROVIDER_FOR_CORRECTIONS = "Returned to Provider for Corrections",
    PENDING_REVIEW_OF_PROVIDER_REVISIONS = "Pending Review of Provider Revisions",
    REPORT_AUTHORIZED = "Report Authorized",
    SUBMITTED_TO_FUNDER = "Submitted to Funder",
    FUNDER_REVISION_REQUESTED = "Funder Revision Requested",
    FUNDER_REVISION_REQUESTED_ADMIN_REVIEW = "Funder Revision Requested - Admin Review",
    AUTHORIZATION_RECEIVED = "Authorization Received",
    REPORT_APPROVED_BY_FUNDER = "Report Approved by Funder",
    REPORT_CLOSED = "Report Closed"
}

export const {
    nameToValue: ClientReportReportStatusMap,
    valueToName: ClientReportReportStatusFromValueMap,
    nameToValueWithDefault: ClientReportReportStatusMapWithDefault
} = EnumToMapping(ClientReportReportStatus, {value: "", label: "All Report Status"})

export const isClientReportStatusInReview = (reportStatus: string) => {
    return ClientReportReportStatusMap.get(reportStatus) === ClientReportReportStatus.REVIEW_IN_PROGRESS ||
        ClientReportReportStatusMap.get(reportStatus) === ClientReportReportStatus.RETURNED_TO_PROVIDER_FOR_CORRECTIONS ||
        ClientReportReportStatusMap.get(reportStatus) === ClientReportReportStatus.PENDING_REVIEW_OF_PROVIDER_REVISIONS ||
        ClientReportReportStatusMap.get(reportStatus) === ClientReportReportStatus.FUNDER_REVISION_REQUESTED_ADMIN_REVIEW;
}

export const isClientReportStatusInAuthorized = (reportStatus: string) => {
    return ClientReportReportStatusMap.get(reportStatus) === ClientReportReportStatus.AUTHORIZATION_RECEIVED ||
        ClientReportReportStatusMap.get(reportStatus) === ClientReportReportStatus.REPORT_AUTHORIZED ||
        ClientReportReportStatusMap.get(reportStatus) === ClientReportReportStatus.REPORT_APPROVED_BY_FUNDER ||
        ClientReportReportStatusMap.get(reportStatus) === ClientReportReportStatus.SUBMITTED_TO_FUNDER ||
        ClientReportReportStatusMap.get(reportStatus) === ClientReportReportStatus.FUNDER_REVISION_REQUESTED;
}


export enum AuthorizationStatus {
    Active = "Active",
    Inactive = "Inactive",
    Pending = "Pending"
}

export const {
    nameToValue: AuthorizationStatusMap,
    nameToValueWithDefault: AuthorizationStatusMapWithDefault
} = EnumToMapping(AuthorizationStatus, {value: "", label: "All Status"});

export enum ParentCaseStatus {
    Active = "Active",
    Inactive = "Inactive"
}

export const {
    nameToValueWithDefault: ParentCaseStatusMapWithDefault
} = EnumToMapping(ParentCaseStatus, {value: "", label: "All Status"});

export const AuthorizationAllServiceType = {
    ...AuthorizationServiceType,
    ...AuthorizationAssessmentType,
    ...FunderAuthorizationSpeciality,
    ...DiagnosticEvaluationType,
}

export const {
    nameToValueWithDefault: AuthorizationAllServiceTypeWithDefault
} = EnumToMapping(AuthorizationAllServiceType, {value: "", label: "All Service Type"}, true);

export const InOfferedAuthorizationAllServiceType = new Map<string, string>(
    Array.from(AuthorizationAllServiceTypeWithDefault.entries()).filter(
        (entry: [string, string]) => !NoLongerOfferedAuthServiceTypes.includes(entry[1] as AuthorizationServiceType | AuthorizationAssessmentType)));

export enum ProviderMetricsReportType {
    Provider_Performance_Metrics = "Provider Performance Metrics",
    Provider_Collaboration_Report = "Provider Collaboration Report"
}

export const {
    nameToValue: ProviderMetricsReportTypeMapNoDefault,
    nameToValueWithDefault: ProviderMetricsReportTypeMap,
} = EnumToMapping(ProviderMetricsReportType, {value: "", label: "Please choose"}, true)

export enum ReportInquiringAboutType {
    GOALS_MET = "% Goals Met",
    ASSESSMENT_REPORT = "Assessment Report",
    CLINICAL_FEEDBACK = "Clinical Feedback",
    FSO = "FSO",
    PROGRESS_REPORT_SUBMISSION = "Progress Report Submission",
    PROVIDER_CSAT_SCORE = "Provider CSAT Score",
    REPORTS_RETURNED = "Reports Returned",
    TRANSFER_RETURN_RATES = "Transfer & Return Rates",
    TRANSFER_CLIENTS = "Transfer Clients"
}

export const {
    nameToValue: ReportInquiringAboutTypeMapNoDefault,
    nameToValueWithDefault: ReportInquiringAboutTypeMap,
} = EnumToMapping(ReportInquiringAboutType, {value: "", label: "Please choose"}, true)

export const belongsToAssessmentReportFamily = (reportType?: string) => {
    if (!reportType) {
        return false;
    }

    return [
        ClientReportReportType.ABA_ASSESSMENT_REPORT,
        ClientReportReportType.CHAT_ASSESSMENT_REPORT,
        ClientReportReportType.CONNECT_ASSESSMENT_REPORT,
        ClientReportReportType.RUBI_ASSESSMENT_REPORT,
        ClientReportReportType.COMPASS_BEHAVIORAL_ASSESSMENT_REPORT,
        ClientReportReportType.CALIBRATE_ASSESSMENT_REPORT,
        ClientReportReportType.DIAGNOSTIC_EVALUATION_REPORT,
        ClientReportReportType.DIAGNOSTIC_EVALUATION_AFTER_VISIT_REPORT,
        ClientReportReportType.PSYCHOTHERAPY_ASSESSMENT_REPORT,
    ].includes(ClientReportReportTypeMap.get(reportType) as ClientReportReportType);
}

export const shouldShowFirstTreatmentOfferedDate = (reportType?: string) => {
    if (!reportType) return;
    return [
        ClientReportReportType.ABA_ASSESSMENT_REPORT,
        ClientReportReportType.ABA_TRANSFER_REEVALUATION_REPORT,
        ClientReportReportType.CHAT_ASSESSMENT_REPORT,
        ClientReportReportType.CONNECT_ASSESSMENT_REPORT,
        ClientReportReportType.COMPASS_BEHAVIORAL_ASSESSMENT_REPORT,
        ClientReportReportType.RUBI_ASSESSMENT_REPORT,
        ClientReportReportType.CALIBRATE_ASSESSMENT_REPORT,
        ClientReportReportType.PSYCHOTHERAPY_ASSESSMENT_REPORT,
    ].includes(ClientReportReportTypeMap.get(reportType) as ClientReportReportType);
}

export const shouldShowTreatmentOffered = (reportType?: string) => {
    if (!reportType) return;
    return [
        ClientReportReportType.ABA_ASSESSMENT_REPORT,
        ClientReportReportType.ABA_TRANSFER_REEVALUATION_REPORT,
        ClientReportReportType.CHAT_ASSESSMENT_REPORT,
        ClientReportReportType.CONNECT_ASSESSMENT_REPORT,
        ClientReportReportType.COMPASS_BEHAVIORAL_ASSESSMENT_REPORT,
        ClientReportReportType.RUBI_ASSESSMENT_REPORT,
        ClientReportReportType.CALIBRATE_ASSESSMENT_REPORT,
        ClientReportReportType.PSYCHOTHERAPY_ASSESSMENT_REPORT,
    ].includes(ClientReportReportTypeMap.get(reportType) as ClientReportReportType);
}

export const shouldCheckPendingAuthorizationByReportType = (reportType?: string): boolean => {
    if (!reportType) return false;
    if (isAddendumReportType(reportType)) return true;
    return [
        ClientReportReportType.CHANGE_HOURS_ADDENDUM,
    ].includes(ClientReportReportTypeMap.get(reportType) as ClientReportReportType);
}

export const isAddendumReportType = (reportType?: string): boolean => {
    if (!reportType) return false;
    return [
        ClientReportReportType.ABA_ADDENDUM_REPORT,
        ClientReportReportType.CHAT_ADDENDUM_REPORT,
        ClientReportReportType.CONNECT_ADDENDUM_REPORT,
        ClientReportReportType.CALIBRATE_ADDENDUM_REPORT,
        ClientReportReportType.COMPASS_BEHAVIORAL_ADDENDUM_REPORT,
        ClientReportReportType.RUBI_ADDENDUM_REPORT,
    ].includes(ClientReportReportTypeMap.get(reportType) as ClientReportReportType);
}

export const shouldShowNewAuthStartDate = (reportType?: string) => {
    return isAddendumReportType(reportType);
}

export const shouldShowLastDayOfService = (reportType?: string) => {
    if (!reportType) return;

    return [
        ClientReportReportType.ABA_DISCHARGE_REPORT,
        ClientReportReportType.ABA_TRANSFER_REPORT,

        ClientReportReportType.CHAT_DISCHARGE_REPORT,
        ClientReportReportType.CHAT_TRANSFER_REPORT,

        ClientReportReportType.CONNECT_DISCHARGE_REPORT,
        ClientReportReportType.CONNECT_TRANSFER_REPORT,

        ClientReportReportType.CALIBRATE_DISCHARGE_REPORT,
        ClientReportReportType.CALIBRATE_TRANSFER_REPORT,

        ClientReportReportType.RUBI_DISCHARGE_REPORT,
        ClientReportReportType.RUBI_TRANSFER_REPORT,

        ClientReportReportType.COMPASS_BEHAVIORAL_DISCHARGE_REPORT,
        ClientReportReportType.COMPASS_BEHAVIORAL_TRANSFER_REPORT,

        ClientReportReportType.SPEECH_THERAPY_DISCHARGE_REPORT,
        ClientReportReportType.SPEECH_THERAPY_TRANSFER_REPORT,

        ClientReportReportType.OCCUPATIONAL_THERAPY_DISCHARGE_REPORT,
        ClientReportReportType.OCCUPATIONAL_THERAPY_TRANSFER_REPORT,

        ClientReportReportType.PHYSICAL_THERAPY_DISCHARGE_REPORT,
        ClientReportReportType.PHYSICAL_THERAPY_TRANSFER_REPORT,
    ].includes(ClientReportReportTypeMap.get(reportType) as ClientReportReportType);
}

export const requireServiceType = (reportType: string) => {
    return [
        ClientReportReportType.ABA_SHORT_ADDENDUM,
        ClientReportReportType.ABA_ASSESSMENT_REPORT,
        ClientReportReportType.ABA_PROGRESS_REPORT,
        ClientReportReportType.ABA_ADDENDUM_REPORT,
        ClientReportReportType.ABA_TRANSFER_REEVALUATION_REPORT,

        ClientReportReportType.CHAT_ASSESSMENT_REPORT,
        ClientReportReportType.CHAT_PROGRESS_REPORT,
        ClientReportReportType.CHAT_ADDENDUM_REPORT,

        ClientReportReportType.CONNECT_ASSESSMENT_REPORT,
        ClientReportReportType.CONNECT_PROGRESS_REPORT,
        ClientReportReportType.CONNECT_ADDENDUM_REPORT,

        ClientReportReportType.CALIBRATE_ASSESSMENT_REPORT,
        ClientReportReportType.CALIBRATE_PROGRESS_REPORT,
        ClientReportReportType.CALIBRATE_ADDENDUM_REPORT,

        ClientReportReportType.COMPASS_BEHAVIORAL_ASSESSMENT_REPORT,
        ClientReportReportType.COMPASS_BEHAVIORAL_PROGRESS_REPORT,
        ClientReportReportType.COMPASS_BEHAVIORAL_ADDENDUM_REPORT,

        ClientReportReportType.RUBI_ASSESSMENT_REPORT,
        ClientReportReportType.RUBI_PROGRESS_REPORT,
        ClientReportReportType.RUBI_ADDENDUM_REPORT,

        ClientReportReportType.PSYCHOTHERAPY_ASSESSMENT_REPORT,
    ].some((type) => type === ClientReportReportTypeMap.get(reportType));
};

export const isAddendumRequestReasonRequired = (reportType?: string) => isAddendumReportFamily(reportType);

export const isAddendumReportFamily = (reportType?: string) => {
    if (!reportType) return;
    return [
        ClientReportReportType.ABA_ADDENDUM_REPORT,
        ClientReportReportType.ABA_SHORT_ADDENDUM,
        ClientReportReportType.CHAT_ADDENDUM_REPORT,
        ClientReportReportType.CONNECT_ADDENDUM_REPORT,
        ClientReportReportType.COMPASS_BEHAVIORAL_ADDENDUM_REPORT,
        ClientReportReportType.RUBI_ADDENDUM_REPORT,
    ].includes(ClientReportReportTypeMap.get(reportType) as ClientReportReportType);
}

export const isProgressReportFamily = (reportType?: string) => {
    if (!reportType) return;
    return [
        ClientReportReportType.ABA_PROGRESS_REPORT,
        ClientReportReportType.CHAT_PROGRESS_REPORT,
        ClientReportReportType.CONNECT_PROGRESS_REPORT,
        ClientReportReportType.COMPASS_BEHAVIORAL_PROGRESS_REPORT,
        ClientReportReportType.RUBI_PROGRESS_REPORT,
    ].includes(ClientReportReportTypeMap.get(reportType) as ClientReportReportType);
}

export const isDiagEvalReport = (reportType?: string) => {
    if (!reportType) return;
    return [
        ClientReportReportType.DIAGNOSTIC_EVALUATION_REPORT,
    ].includes(ClientReportReportTypeMap.get(reportType) as ClientReportReportType);
}

export function isShortAddendumReport(reportType: string) {
    return ClientReportReportTypeMap.get(reportType) === ClientReportReportType.ABA_SHORT_ADDENDUM;
}

export function isDEReport(reportType: string) {
    return ClientReportReportTypeMap.get(reportType) === ClientReportReportType.DIAGNOSTIC_EVALUATION_REPORT;
}

export enum WasClientAssignedToProvider {
    YES = 'Yes',
    NO = 'No'
}

export const {
    nameToValue: WasClientAssignedToProviderMap,
} = EnumToMapping(WasClientAssignedToProvider, {value: "", label: ""})

export enum TreatmentOfferedOption {
    YES = 'Yes',
    NO = 'No'
}

export const {
    nameToValue: TreatmentOfferedOptionMap,
} = EnumToMapping(TreatmentOfferedOption, {value: "", label: ""})

export enum ReportServiceType {
    ABA_Parent_Training = 'ABA_Parent_Training',
    ABA_Services_Severe_Behavior = 'ABA_Services_Severe_Behavior',
    Group_ABA = 'Group_ABA',
    Parent_Led_ABA = 'Parent_Led_ABA',
    Social_Skills_Group = 'Social_Skills_Group',
    Traditional_ABA = 'Traditional_ABA',
    ThreeTierOrParentTraining = 'Three_Tier_OR_Parent_Training',
    ThreeTierOrParentLed = 'Three_Tier_OR_Parent_Led',
    Chat = 'Chat',
    Connect = 'Connect',
    Calibrate = "Calibrate",
    Compass_Behavioral = "Compass_Behavioral",
    Compass_Psychotherapy = "Compass_Psychotherapy",
    Psychotherapy = "Psychotherapy",
    RUBI = "RUBI",
}

export const serviceTypeMapping = {
    [ReportServiceType.ABA_Parent_Training]: 'ABA Parent Training',
    // the correct one on SF is PLURAL (ABA Services Severe BehaviorS <--)
    [ReportServiceType.ABA_Services_Severe_Behavior]: 'ABA Services Severe Behaviors',
    [ReportServiceType.ThreeTierOrParentTraining]: '3-Tier ABA OR ABA Parent Training',
    [ReportServiceType.ThreeTierOrParentLed]: '3-Tier ABA OR Parent Led ABA',
    [ReportServiceType.Group_ABA]: 'Group ABA',
    [ReportServiceType.Parent_Led_ABA]: 'Parent Led ABA',
    [ReportServiceType.Social_Skills_Group]: 'Social Skills Group',
    [ReportServiceType.Traditional_ABA]: '3-Tier ABA',
    [ReportServiceType.Chat]: 'Chat',
    [ReportServiceType.Connect]: 'Connect',
    [ReportServiceType.RUBI]: 'RUBI',
    [ReportServiceType.Calibrate]: 'Calibrate ABA',
    [ReportServiceType.Compass_Behavioral]: 'Compass-Behavioral',
    // [ReportServiceType.Compass_Psychotherapy]: 'Compass Psychotherapy',
    // [ReportServiceType.Psychotherapy]: 'Psychotherapy',
} as const;

export const ReportServiceTypeValueToKey = Object.freeze(Object.fromEntries(Object.entries(serviceTypeMapping).map((mapping) => mapping.reverse())));

export type HourField = keyof (ClientReportSubmitClientOfferHourInput & ClientReportSubmitClinicalRecommendationHourInput);

const mapping: Map<ReportServiceType, HourField[]> = new Map([
    [
        ReportServiceType.Traditional_ABA,
        [
            'practitionerParaprofessionalHoursMin', 'practitionerParaprofessionalHoursMax',
            'practitionerMidLevelHoursMin', 'practitionerMidLevelHoursMax',
            'practitionerHighLevelHoursMin', 'practitionerHighLevelHoursMax',

            'offeredParaprofessionalHours', 'offeredMidLevelHours', 'offeredHighLevelHours',
            'currentOfferedParaprofessionalHours', 'currentOfferedMidLevelHours', 'currentOfferedHighLevelHours',
        ],
    ],
    [
        ReportServiceType.ABA_Parent_Training,
        [
            'caregiverMidLevelHoursMin', 'caregiverMidLevelHoursMax',
            'caregiverHighLevelHoursMin', 'caregiverHighLevelHoursMax',

            'offeredMidLevelHours', 'offeredHighLevelHours',
            'currentOfferedMidLevelHours', 'currentOfferedHighLevelHours',
        ],
    ],
    [
        ReportServiceType.Social_Skills_Group,
        [
            'practitionerMidLevelHoursMin', 'practitionerMidLevelHoursMax',
            'practitionerHighLevelHoursMin', 'practitionerHighLevelHoursMax',
            'practitionerGroupCareHoursMin', 'practitionerGroupCareHoursMax',

            'offeredMidLevelHours', 'offeredHighLevelHours', 'offeredGroupCareHours',
            'currentOfferedMidLevelHours', 'currentOfferedHighLevelHours', 'currentOfferedGroupCareHours',
        ],
    ],
    [
        ReportServiceType.Compass_Behavioral,
        [
            'caregiverHighLevelHoursMin', 'caregiverHighLevelHoursMax',
            'offeredHighLevelHours'
        ],
    ],
]);

mapping.set(ReportServiceType.ABA_Services_Severe_Behavior, mapping.get(ReportServiceType.Traditional_ABA)!);

mapping.set(ReportServiceType.Parent_Led_ABA, mapping.get(ReportServiceType.ABA_Parent_Training)!);
mapping.set(ReportServiceType.Chat, mapping.get(ReportServiceType.ABA_Parent_Training)!);
mapping.set(ReportServiceType.Connect, mapping.get(ReportServiceType.ABA_Parent_Training)!);
mapping.set(ReportServiceType.Calibrate, mapping.get(ReportServiceType.ABA_Parent_Training)!);

mapping.set(ReportServiceType.Group_ABA, mapping.get(ReportServiceType.Social_Skills_Group)!);

mapping.set(ReportServiceType.ThreeTierOrParentLed, _.union(mapping.get(ReportServiceType.ABA_Parent_Training)!, mapping.get(ReportServiceType.Traditional_ABA)!));
mapping.set(ReportServiceType.ThreeTierOrParentTraining, _.union(mapping.get(ReportServiceType.ABA_Parent_Training)!, mapping.get(ReportServiceType.Traditional_ABA)!));
mapping.set(ReportServiceType.Chat, mapping.get(ReportServiceType.ABA_Parent_Training)!);
mapping.set(ReportServiceType.Connect, mapping.get(ReportServiceType.ABA_Parent_Training)!);
mapping.set(ReportServiceType.Calibrate, mapping.get(ReportServiceType.ABA_Parent_Training)!);

mapping.set(ReportServiceType.RUBI, mapping.get(ReportServiceType.Compass_Behavioral)!);

export const serviceTypeMappedToHourFields = Object.freeze(mapping);

// BHPNPP-2084
export const reportTypesByAuthInfo = (authRecordType: string, authAssessmentType: string, authSpeciality: string, allServiceTypes: string[]) => {
    let reportTypes = new Map<string, string>();
    switch (AuthorizationRecordTypeMap.get(authRecordType)) {
        case AuthorizationRecordType.ABA_Transfer_Reevaluation:
            return getEnumNameValueMap(ClientReportReportType, ClientReportReportType.ABA_TRANSFER_REEVALUATION_REPORT);
        case AuthorizationRecordType.ABA_Assessment_Authorization:
            switch (AuthorizationAssessmentTypeMap.get(authAssessmentType)) {
                case AuthorizationAssessmentType.Comprehensive_ABA_Assessment:
                case AuthorizationAssessmentType.Social_Skills_Group_Assessment:
                    return AssessmentAuthClientReportTypeMap;
                case AuthorizationAssessmentType.Chat_Assessment:
                    return getEnumNameValueMap(ClientReportReportType, ClientReportReportType.CHAT_ASSESSMENT_REPORT);
                case AuthorizationAssessmentType.Connect_Assessment:
                    return getEnumNameValueMap(ClientReportReportType, ClientReportReportType.CONNECT_ASSESSMENT_REPORT);
                case AuthorizationAssessmentType.RUBI_Assessment:
                    return getEnumNameValueMap(ClientReportReportType, ClientReportReportType.RUBI_ASSESSMENT_REPORT);
                case AuthorizationAssessmentType.Compass_Behavioral_Assessment:
                    return getEnumNameValueMap(ClientReportReportType, ClientReportReportType.COMPASS_BEHAVIORAL_ASSESSMENT_REPORT);
                case AuthorizationAssessmentType.Calibrate_ABA_Assessment:
                    return getEnumNameValueMap(ClientReportReportType, ClientReportReportType.CALIBRATE_ASSESSMENT_REPORT);
                default:
                    return AssessmentAuthClientReportTypeMap;
            }
        case AuthorizationRecordType.Psychotherapy_Assessment:
            return getEnumNameValueMap(ClientReportReportType, ClientReportReportType.PSYCHOTHERAPY_ASSESSMENT_REPORT);
        case AuthorizationRecordType.ABA_Treatment_Authorization_Group_Care:
            if (intersection(allServiceTypes, [
                ReportServiceType.Traditional_ABA,
                ReportServiceType.ThreeTierOrParentLed,
                ReportServiceType.ThreeTierOrParentTraining,
                ReportServiceType.Parent_Led_ABA,
                ReportServiceType.ABA_Parent_Training,
                ReportServiceType.Social_Skills_Group,
                ReportServiceType.Group_ABA]).length > 0) {
                reportTypes = new Map([...reportTypes, ...TreatmentAuthClientReportTypeMap]);
            }
            break;
        case AuthorizationRecordType.ABA_Treatment_Authorization:
            if (intersection(allServiceTypes, [
                ReportServiceType.Traditional_ABA,
                ReportServiceType.ThreeTierOrParentLed,
                ReportServiceType.ThreeTierOrParentTraining,
                ReportServiceType.Parent_Led_ABA,
                ReportServiceType.ABA_Parent_Training,
                ReportServiceType.Social_Skills_Group,
                ReportServiceType.Group_ABA]).length > 0) {
                reportTypes = new Map([...reportTypes, ...TreatmentAuthClientReportTypeMap]);
            }
            if (intersection(allServiceTypes, [ReportServiceType.Chat]).length > 0) {
                reportTypes = new Map([...reportTypes, ...TreatmentAuthChatReportTypeMap]);
            }
            if (intersection(allServiceTypes, [ReportServiceType.Connect]).length > 0) {
                reportTypes = new Map([...reportTypes, ...TreatmentAuthConnectReportTypeMap]);
            }
            if (intersection(allServiceTypes, [ReportServiceType.Compass_Behavioral]).length > 0) {
                reportTypes = new Map([...reportTypes, ...TreatmentAuthCompassReportTypeMap]);
            }
            if (intersection(allServiceTypes, [ReportServiceType.RUBI]).length > 0) {
                reportTypes = new Map([...reportTypes, ...TreatmentAuthRUBIReportTypeMap]);
            }
            if (intersection(allServiceTypes, [ReportServiceType.Calibrate]).length > 0) {
                reportTypes = new Map([...reportTypes, ...TreatmentAuthCalibreReportTypeMap]);
            }
            break;
        case AuthorizationRecordType.Psychotherapy_Treatment:
            if (intersection(allServiceTypes, [ReportServiceType.Psychotherapy]).length > 0) {
                reportTypes = new Map([...reportTypes, ...getEnumNameValueMap(ClientReportReportType, ClientReportReportType.PSYCHOTHERAPY_PROGRESS_REPORT)]);
            }
            if (intersection(allServiceTypes, [ReportServiceType.Compass_Psychotherapy]).length > 0) {
                reportTypes = new Map([...reportTypes, ...getEnumNameValueMap(ClientReportReportType, ClientReportReportType.COMPASS_PSYCHOTHERAPY_PROGRESS_REPORT)]);
            }
            break;
        case AuthorizationRecordType.Therapy_Treatment_Authorization:
            if (isEmpty(authSpeciality)) {
                break;
            }
            if (authSpeciality.includes('Speech Therapy')) {
                reportTypes = new Map([...reportTypes, ...TreatmentAuthSpeechTherapyReportTypeMap]);
            }
            if (authSpeciality.includes('Occupational Therapy')) {
                reportTypes = new Map([...reportTypes, ...TreatmentAuthOccupationalTherapyReportTypeMap]);
            }
            if (authSpeciality.includes('Physical Therapy')) {
                reportTypes = new Map([...reportTypes, ...TreatmentAuthPhysicalTherapyReportTypeMap]);
            }
            break;
        case AuthorizationRecordType.Diagnostic_Evaluation_Authorization:
            reportTypes = new Map([...reportTypes, ...getEnumNameValueMap(ClientReportReportType, ClientReportReportType.DIAGNOSTIC_EVALUATION_REPORT)]);
            reportTypes = new Map([...reportTypes, ...getEnumNameValueMap(ClientReportReportType, ClientReportReportType.DIAGNOSTIC_EVALUATION_AFTER_VISIT_REPORT)]);
            break;
    }

    return reportTypes;
}

export const authorizationFieldFormattedValue = (authorization: AuthorizationInfo, attr: Paths<AuthorizationInfo>) => {

    const dateFields = [
        'startDate', 'endDate', 'reportDueDate',
        'startDate1', 'endDate1',
        'startDate2', 'endDate2',
        'startDate3', 'endDate3',
        'startDate4', 'endDate4',
        'startDate5', 'endDate5',
        'startDate6', 'endDate6',
    ];
    const authValue = get(authorization, attr);
    if (attr === 'latestSubmittedClientReport.submittedAt' && authValue) {
        return formatDate(parseISO(authValue));
    }
    if (attr === 'latestSubmittedClientReport.reportStatus' && authValue) {
        return ClientReportReportStatusMap.get(authValue);
    }


    if (attr === 'recordType' && authValue) {
        if (AuthorizationRecordTypeMap.get(authValue) !== AuthorizationRecordType.Therapy_Treatment_Authorization) {
            return AuthorizationRecordTypeMap.get(authValue);
        }

        return FunderAuthorizationRecordTypeMap.get(authorization.funderAuthorizationRecordType);
    }

    return !!authValue && dateFields.includes(attr) ? formatDateStr(authValue.toString()) : authValue;
}

// { '3-Tier ABA' -> 'Traditional_ABA', ... }
export const serviceTypeInverse = Object.fromEntries(Object.entries(serviceTypeMapping).map((mapping) => mapping.reverse()));
